/**
 * Selectable wind presets for the wind slider.
 *
 * The slider sets the TIA-222 **3-second GUST** speed in m/s (see
 * docs/ENGINEERING.md §1 and src/physics/wind.ts, where q = 0.613·Kz·V²).
 * So the load-bearing value here is `gust`. `sustained` is the ~10-min /
 * 1-min mean the figure is usually quoted at (e.g. Beaufort, Saffir-Simpson),
 * given for reference/labelling only.
 *
 * Gust-vs-sustained: an open-terrain 3-s gust runs ~1.2–1.5× the sustained
 * mean (gust factor). Tropical-cyclone practice commonly uses ~1.3× over
 * open water. Where a source quotes a mean, gust is derived with a factor in
 * that range; where a source quotes a measured gust, that value is used
 * directly and sustained is back-estimated (÷~1.3) or omitted.
 *
 * Full research writeup + sources: docs/WIND_PRESETS.md
 *
 * NOTE: several top presets exceed the current 90 m/s slider max — raise the
 * slider max to ~120 m/s to fit the record gust (see docs/WIND_PRESETS.md).
 */

export interface WindPreset {
  id: string;
  name: string;
  description: string;
  /** m/s, 3-s gust — the value the slider sets. */
  gust: number;
  /** m/s, sustained mean (Beaufort ~10-min, cyclone ~1-min). Reference only. */
  sustained?: number;
  region?: string;
}

/** Ordered ascending by gust. */
export const WIND_PRESETS: WindPreset[] = [
  {
    // Beaufort 4 "moderate breeze" (5.5–7.9 m/s mean). Leaves & small
    // branches move; flags flap. Gust ≈ 1.3× mean. Source: WMO/NOAA Beaufort.
    id: "moderate-breeze",
    name: "Moderate breeze",
    description:
      "Beaufort 4. Flags flap, dust and loose paper lift. A calm working day on the tower.",
    gust: 9,
    sustained: 6.7,
  },
  {
    // Beaufort 6 "strong breeze" (10.8–13.8 m/s mean). Large branches move,
    // umbrellas hard to use. Gust ≈ 1.3× mean. Source: WMO/NOAA Beaufort.
    id: "strong-breeze",
    name: "Strong breeze",
    description:
      "Beaufort 6. Large branches sway, whistling in guy wires — you feel the tower work.",
    gust: 16,
    sustained: 12.3,
  },
  {
    // Beaufort 9 "strong/severe gale" (20.8–24.4 m/s mean). Slight structural
    // damage (chimney pots, slates). Gust ≈ 1.35× mean. Source: WMO Beaufort.
    id: "gale",
    name: "Gale",
    description:
      "Beaufort 9. Branches break, minor structural damage begins. Serious wind for any mast.",
    gust: 31,
    sustained: 22.6,
  },
  {
    // Typical US inland design (TIA-222-G / ASCE 7, 3-s gust) ≈ 40 m/s (90 mph).
    // Also a ROHN catalog point. Source: docs/ENGINEERING.md §1; TIA-222-G.
    id: "design-inland",
    name: "Design: US inland",
    description:
      "TIA-222-G basic 3-s gust for most of the US interior (~90 mph). What towers are built to survive.",
    gust: 40,
    sustained: 31,
    region: "US inland",
  },
  {
    // AS1170.2 non-cyclonic regions A/B, ~500-yr regional gust ~45 m/s
    // (VR ≈ 45 m/s). Source: AS/NZS 1170.2 Region A/B.
    id: "as1170-ab",
    name: "Design: AS1170 A/B",
    description:
      "Australian non-cyclonic design gust (regions A/B). Typical southern/inland Australia.",
    gust: 45,
    sustained: 33,
    region: "Australia A/B (non-cyclonic)",
  },
  {
    // Coastal / hurricane design lower bound ≈ 50 m/s (110 mph) 3-s gust.
    // Source: docs/ENGINEERING.md §1; ASCE 7 coastal maps.
    id: "design-coastal",
    name: "Design: coastal/hurricane",
    description:
      "Coastal high-wind design gust (~110 mph). Towers in hurricane-exposed regions.",
    gust: 50,
    sustained: 38,
    region: "US coastal",
  },
  {
    // Saffir-Simpson Cat 1: sustained 33–42 m/s (1-min). Take midpoint ~38 m/s,
    // gust ≈ 1.3× ≈ 49 m/s. Source: NOAA NHC Saffir-Simpson scale.
    id: "hurricane-cat1",
    name: "Hurricane Cat 1",
    description:
      "Saffir-Simpson Category 1. Sustained 33–42 m/s. Damage to unanchored structures.",
    gust: 49,
    sustained: 38,
  },
  {
    // Saffir-Simpson Cat 2: sustained 43–49 m/s. Midpoint ~46 m/s, gust ≈ 1.3×.
    // Source: NOAA NHC.
    id: "hurricane-cat2",
    name: "Hurricane Cat 2",
    description:
      "Saffir-Simpson Category 2. Sustained 43–49 m/s. Extensive damage, extended power loss.",
    gust: 60,
    sustained: 46,
  },
  {
    // AS1170.2 cyclonic regions C/D, ~500-yr regional gust ~66 m/s (region D
    // VR up to ~70 m/s). Source: AS/NZS 1170.2 Region C/D.
    id: "as1170-cd",
    name: "Design: AS1170 C/D",
    description:
      "Australian cyclonic design gust (regions C/D). Northern coastal Australia — the harshest AS1170 case.",
    gust: 66,
    sustained: 50,
    region: "Australia C/D (cyclonic)",
  },
  {
    // Saffir-Simpson Cat 3: sustained 50–58 m/s. Midpoint ~54 m/s, gust ≈ 1.3×.
    // Source: NOAA NHC.
    id: "hurricane-cat3",
    name: "Hurricane Cat 3",
    description:
      "Saffir-Simpson Category 3 (major). Sustained 50–58 m/s. Devastating damage.",
    gust: 70,
    sustained: 54,
  },
  {
    // Saffir-Simpson Cat 4: sustained 58–70 m/s. Midpoint ~64 m/s, gust ≈ 1.3×.
    // Source: NOAA NHC.
    id: "hurricane-cat4",
    name: "Hurricane Cat 4",
    description:
      "Saffir-Simpson Category 4. Sustained 58–70 m/s. Catastrophic; most structures fail.",
    gust: 83,
    sustained: 64,
  },
  {
    // Saffir-Simpson Cat 5: sustained ≥70 m/s. Take ~75 m/s reference,
    // gust ≈ 1.3× ≈ 98 m/s. Source: NOAA NHC.
    id: "hurricane-cat5",
    name: "Hurricane Cat 5",
    description:
      "Saffir-Simpson Category 5. Sustained ≥70 m/s. Total roof/wall failure; the top of the storm scale.",
    gust: 98,
    sustained: 75,
  },
  {
    // Mount Washington, NH: 231 mph = 103 m/s gust (12 Apr 1934). Long-standing
    // record for a surface station outside a tropical cyclone/tornado.
    // Source: Mount Washington Observatory.
    id: "mount-washington",
    name: "Mt Washington record",
    description:
      "231 mph (103 m/s) measured on Mount Washington, NH, 1934 — the classic surface-wind record.",
    gust: 103,
    region: "Mount Washington, NH, USA",
  },
  {
    // EF5 tornado threshold ~ >200 mph (~90+ m/s) 3-s gust; violent tornado
    // cores estimated well above. Use ~110 m/s as an EF5 reference point.
    // Source: NWS Enhanced Fujita scale.
    id: "tornado-ef5",
    name: "EF5 tornado",
    description:
      "Enhanced Fujita 5 tornado (>90 m/s; violent cores far higher). Nothing conventional survives the core.",
    gust: 110,
    region: "EF5 tornado core",
  },
  {
    // Barrow Island, WA: 408 km/h = 113 m/s gust during Cyclone Olivia,
    // 10 Apr 1996 — WMO-recognised highest surface wind gust not in a tornado.
    // Source: WMO / Bureau of Meteorology.
    id: "barrow-island",
    name: "Barrow Is. world record",
    description:
      "408 km/h (113 m/s) at Barrow Island, WA during Cyclone Olivia, 1996 — highest surface gust ever recorded (WMO).",
    gust: 113,
    region: "Barrow Island, WA, Australia",
  },
];
